import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert } from 'rsuite';

import { useDebounce } from '../../../../../hooks';
import { deleteSavedFilters, getSavedFilters, saveFilters } from '../services';
import { useVisaoGeralElasticidadeStore } from '../VisaoGeralElasticidade.store';
import type { VisaoGeralElasticidadeFiltroSalvoBody, VisaoGeralElasticidadeFiltroSalvoResponse, VisaoGeralElasticidadeTableBody } from '../types';

type SavedFilterOption = {
  label: string;
  value: number;
};

const normalizeSavedFiltersResponse = (response: unknown): VisaoGeralElasticidadeFiltroSalvoResponse[] => {
  if (Array.isArray(response)) return response;

  if (response && typeof response === 'object' && 'data' in response && Array.isArray((response as { data: unknown }).data)) {
    return (response as { data: VisaoGeralElasticidadeFiltroSalvoResponse[] }).data;
  }

  return [];
};

const mapToOptions = (items: VisaoGeralElasticidadeFiltroSalvoResponse[]): SavedFilterOption[] =>
  items.map(item => ({
    label: item.nomeFiltroSalvo,
    value: item.idFiltroSalvo,
  }));

const hasAtLeastOneFilterSelected = (tableBody: VisaoGeralElasticidadeTableBody) =>
  Boolean(tableBody.query) ||
  tableBody.productBarcodes.length > 0 ||
  tableBody.manufacturers.length > 0 ||
  tableBody.brands.length > 0 ||
  tableBody.categories.length > 0 ||
  tableBody.sections.length > 0 ||
  tableBody.contents.length > 0 ||
  Boolean(tableBody.segmentation) ||
  Boolean(tableBody.subSegmentation) ||
  Boolean(tableBody.region);

const buildSaveFiltersBody = (filterName: string): VisaoGeralElasticidadeFiltroSalvoBody => {
  const tableBody = useVisaoGeralElasticidadeStore.getState().tableBody;

  return {
    nomeFiltroSalvo: filterName,
    filtros: {
      query: tableBody.query,
      productBarcodes: tableBody.productBarcodes,
      manufacturers: tableBody.manufacturers,
      brands: tableBody.brands,
      categories: tableBody.categories,
      sections: tableBody.sections,
      contents: tableBody.contents,
      segmentation: tableBody.segmentation,
      subSegmentation: tableBody.subSegmentation,
      region: tableBody.region,
    },
  };
};

export const useSaveFilters = () => {
  const [savedFiltersList, setSavedFiltersList] = useState<VisaoGeralElasticidadeFiltroSalvoResponse[]>([]);
  const [savedFiltersOptions, setSavedFiltersOptions] = useState<SavedFilterOption[]>([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState<VisaoGeralElasticidadeFiltroSalvoResponse | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filterName, setFilterName] = useState('');

  const setTableBody = useVisaoGeralElasticidadeStore.getState().setTableBody;
  const debounce = useDebounce(500);

  const searchMutation = useMutation({
    mutationFn: (query: string) => getSavedFilters(query),
    onSuccess: response => {
      const items = normalizeSavedFiltersResponse(response);
      setSavedFiltersList(items);
      setSavedFiltersOptions(mapToOptions(items));
    },
  });

  const { mutate: searchSavedFilters } = searchMutation;

  const saveMutation = useMutation({
    mutationFn: (data: VisaoGeralElasticidadeFiltroSalvoBody) => saveFilters(data),
    onSuccess: () => {
      Alert.success('Filtro salvo com sucesso');
      setShowSaveModal(false);
      setFilterName('');
      searchSavedFilters('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteSavedFilters(id),
    onSuccess: () => {
      Alert.success('Filtro excluído com sucesso');
      setSelectedSavedFilter(null);
      searchSavedFilters('');
    },
  });

  const handleOpenSavedFilters = useCallback(() => {
    searchSavedFilters('');
  }, [searchSavedFilters]);

  const handleSearchSavedFilters = useCallback(
    (query: string) => {
      debounce(() => {
        searchSavedFilters(query);
      });
    },
    [debounce, searchSavedFilters],
  );

  const handleOpenSaveModal = useCallback(() => {
    const tableBody = useVisaoGeralElasticidadeStore.getState().tableBody;

    if (!hasAtLeastOneFilterSelected(tableBody)) {
      Alert.warning('Selecione ao menos um filtro para salvar', 5000);
      return;
    }

    setFilterName('');
    setShowSaveModal(true);
  }, []);

  const handleCloseSaveModal = useCallback(() => {
    setShowSaveModal(false);
    setFilterName('');
  }, []);

  const handleChangeFilterName = useCallback((value: string) => {
    setFilterName(value);
  }, []);

  const handleSaveFilter = useCallback(() => {
    if (!filterName.trim()) {
      Alert.warning('Preencha o campo do nome do filtro', 5000);
      return;
    }

    saveMutation.mutate(buildSaveFiltersBody(filterName.trim()));
  }, [filterName, saveMutation]);

  const handleSelectSavedFilter = useCallback(
    (id: number): VisaoGeralElasticidadeTableBody | null => {
      const savedFilter = savedFiltersList.find(item => item.idFiltroSalvo === id);
      if (!savedFilter) return null;

      setSelectedSavedFilter(savedFilter);

      const currentTableBody = useVisaoGeralElasticidadeStore.getState().tableBody;
      const mergedTableBody: VisaoGeralElasticidadeTableBody = { ...currentTableBody, ...savedFilter.filtros };
      setTableBody(mergedTableBody);

      return mergedTableBody;
    },
    [savedFiltersList, setTableBody],
  );

  const handleClearSavedFilter = useCallback(() => {
    setSelectedSavedFilter(null);
  }, []);

  const handleDeleteSavedFilter = useCallback(() => {
    if (!selectedSavedFilter?.idFiltroSalvo) {
      Alert.warning('Selecione um filtro salvo para excluir', 5000);
      return;
    }

    deleteMutation.mutate(selectedSavedFilter.idFiltroSalvo);
  }, [deleteMutation, selectedSavedFilter]);

  useEffect(() => {
    searchSavedFilters('');
  }, []);

  return {
    savedFiltersOptions,
    selectedSavedFilterId: selectedSavedFilter?.idFiltroSalvo ?? null,
    showSaveModal,
    filterName,
    isLoadingSavedFilters: searchMutation.isPending,
    isSavingFilter: saveMutation.isPending,
    isDeletingFilter: deleteMutation.isPending,
    handleOpenSavedFilters,
    handleSearchSavedFilters,
    handleOpenSaveModal,
    handleCloseSaveModal,
    handleChangeFilterName,
    handleSaveFilter,
    handleSelectSavedFilter,
    handleClearSavedFilter,
    handleDeleteSavedFilter,
  };
};
